import { readContextChatStream } from '../ContextChatStream'
import { notifyPlanAccessNotice } from '../PlanAccessNotice'
import localApi, { refreshSessionToken } from '../../services/localApi'

export interface NoraConversation {
  id: string
  title: string
  createdAt: string
  updatedAt: string
}

export interface NoraKnowledgeSource {
  sourceId: string
  title: string
  heading: string
  sourceUrl?: string | null
}

export interface NoraMessage {
  id: string
  turnId: string
  role: 'user' | 'assistant'
  content: string
  createdAt: string
  state?: 'failed' | 'streaming'
  turnStatus?: 'pending' | 'completed' | 'failed'
  sources?: NoraKnowledgeSource[]
}

interface Envelope<T> { success: boolean, data: T }
export interface NoraConversationList {
  accountId: string
  userId: string
  conversations: NoraConversation[]
  hasMore: boolean
}
export interface NoraMessageList {
  conversation: NoraConversation
  messages: NoraMessage[]
  hasMore: boolean
}

function isRecord (value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function parseMessage (value: unknown): NoraMessage {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.turnId !== 'string' ||
    (value.role !== 'user' && value.role !== 'assistant') || typeof value.content !== 'string' ||
    typeof value.createdAt !== 'string') throw new Error('A resposta da Nora está incompleta.')
  return {
    id: value.id,
    turnId: value.turnId,
    role: value.role,
    content: value.content,
    createdAt: value.createdAt,
    turnStatus: 'completed',
    sources: Array.isArray(value.sources)
      ? value.sources.filter((source): source is NoraKnowledgeSource => isRecord(source) &&
        typeof source.sourceId === 'string' && typeof source.title === 'string' && typeof source.heading === 'string' &&
        (source.sourceUrl == null || typeof source.sourceUrl === 'string'))
      : []
  }
}

const PATH = '/v1/nora'

export const noraApi = {
  async listConversations (offset = 0, signal?: AbortSignal): Promise<NoraConversationList> {
    const response = await localApi.get<Envelope<NoraConversationList>>(`${PATH}/conversations`, { params: { offset }, signal })
    return response.data.data
  },
  async createConversation (): Promise<NoraConversation> {
    const response = await localApi.post<Envelope<NoraConversation>>(`${PATH}/conversations`)
    return response.data.data
  },
  async getMessages (id: string, before?: string, signal?: AbortSignal): Promise<NoraMessageList> {
    const response = await localApi.get<Envelope<NoraMessageList>>(`${PATH}/conversations/${id}/messages`, { params: { before }, signal })
    return { ...response.data.data, messages: response.data.data.messages.map(item => ({ ...item, state: item.state ?? undefined })) }
  },
  async suggestions (turnId: string, signal?: AbortSignal): Promise<string[]> {
    const response = await localApi.post<Envelope<{ suggestions: string[] }>>(`${PATH}/turns/${turnId}/suggestions`, undefined, { signal })
    return response.data.data.suggestions
  },
  async send (id: string, message: string, operationId: string, onDelta: (text: string) => void, signal: AbortSignal): Promise<NoraMessage> {
    const base = String(localApi.defaults.baseURL ?? '').replace(/\/+$/, '')
    const url = `${base}${PATH}/conversations/${id}/chat`
    const request = async (token: string | null): Promise<Response> => await fetch(url, {
      method: 'POST',
      signal,
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ message, operationId })
    })
    const token = window.localStorage.getItem('sessionToken')
    let response = await request(token)
    if (response.status === 401) response = await request(await refreshSessionToken(token))
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null)
      const detail = isRecord(body) ? body.detail : null
      notifyPlanAccessNotice(detail)
      throw new Error(typeof detail === 'string'
        ? detail
        : isRecord(detail) && typeof detail.message === 'string'
          ? detail.message
          : 'Não foi possível conversar com a Nora.')
    }
    const result = await readContextChatStream(response, onDelta)
    return parseMessage(result.message)
  }
}
